import { Button, Col, DatePicker, Form, Input, Modal, Row, Select } from 'antd';
import { FC, useEffect, useState } from 'react';
import { RiCloseFill } from 'react-icons/ri';
import { useBookings } from './useBookings';
import { useIntl } from 'react-intl';
import { BookRequest } from 'src/api/bookings';
import { useCenters } from '../centers/useCenters';
import { useColaborators } from '../colaborators/useColaborators';
import { useServices } from '../services/useServices';
import { useCustomers } from '../final-customers/useCustomers';
import { useAvailableBookingTime } from './available-booking-time';
import dayjs, { Dayjs } from 'dayjs';

export interface Props {
  open: boolean;
  handleCancel: () => void;
}

export const baseRules = [
  { required: true, message: 'Este campo é obrigatório' },
];

const BookForm: FC<Props> = (props) => {
  const { book } = useBookings();

  const handleConfirm = (values: BookRequest) => {
    book(values);
    form.resetFields();

    props.handleCancel();
  };

  const [form] = Form.useForm();

  let initialValues = {};

  const [selectedCustomer, setSelectedCustomer] = useState<string>();
  const [selectedCenter, setSelectedCenter] = useState<string>();
  const [selectedColaborator, setSelectedColaborator] = useState<string>();
  const [selectedService, setSelectedService] = useState<string>();
  const { centersQuery } = useCenters();
  const { customersQuery } = useCustomers();
  const { colaboratorsQuery } = useColaborators();
  const { servicesQuery } = useServices();
  const disabledTime = useAvailableBookingTime(selectedColaborator || null);
  const handleCustomerChange = (customerID: string) => {
    setSelectedCustomer(customerID);
    if (!customerID) {
      setSelectedCenter(undefined);
      return;
    }

    if (selectedCenter) {
      return;
    }
    const selectedCustomerDetails = customersQuery.data?.find(
      (c) => c.id === customerID,
    );

    if (!customerID || !selectedCustomerDetails?.centerID) {
      return;
    }
    form.setFieldValue('centerID', selectedCustomerDetails.centerID);
  };

  const updateEndDate = (
    serviceDurationInMinutes: number,
    startDate: string | Dayjs,
  ) => {
    const endDate = dayjs(startDate).add(serviceDurationInMinutes, 'minutes');
    form.setFieldValue('endDate', endDate);
  };

  const handleDateSelected = (date: Dayjs) => {
    const durationInMinutes = form.getFieldValue('durationInMinutes');
    if (!durationInMinutes) {
      return;
    }

    updateEndDate(Number(durationInMinutes), date);
  };
  const handleServiceSelected = (serviceID: string) => {
    setSelectedService(serviceID);

    if (!serviceID) {
      return;
    }

    const selectedService = servicesQuery.data?.find((s) => s.id === serviceID);
    if (!selectedService) {
      return;
    }
    form.setFieldValue(
      'durationInMinutes',
      `${selectedService.durationInMinutes}`,
    );

    const date = form.getFieldValue('date');
    if (!date) {
      return;
    }

    updateEndDate(selectedService.durationInMinutes, date);
  };
  const intl = useIntl();
  useEffect(() => {}, [selectedCustomer, customersQuery, selectedCenter]);

  return (
    <Modal
      title={
        <h5 className='hp-mb-0'>{intl.formatMessage({ id: 'create' })}</h5>
      }
      width={416}
      open={props.open}
      onCancel={props.handleCancel}
      footer={<></>}
      closeIcon={
        <RiCloseFill
          className='remix-icon hp-text-color-black-100 hp-text-color-dark-0'
          size={24}
        />
      }
    >
      <Form
        onFinish={handleConfirm}
        form={form}
        layout='vertical'
        name='basic'
        initialValues={initialValues}
      >
        <Form.Item required rules={baseRules} label='Cliente' name='customerID'>
          <Select
            placeholder='Selecione um cliente'
            onChange={handleCustomerChange}
          >
            {customersQuery.data?.map((c) => (
              <Select.Option value={c.id} key={c.id}>
                {c.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          shouldUpdate
          required
          rules={baseRules}
          label='Centro'
          name='centerID'
        >
          <Select
            placeholder='Selecione um centro'
            onChange={setSelectedCenter}
            value={selectedCenter}
            options={centersQuery.data?.centers.map((c) => ({
              value: c.id,
              label: <span>{c.name}</span>,
            }))}
          />
        </Form.Item>

        <Form.Item
          required
          rules={baseRules}
          label='Colaborador'
          name='colaboratorID'
        >
          <Select
            placeholder='Selecione um colaborador'
            onChange={setSelectedColaborator}
          >
            {colaboratorsQuery.data?.map((c) => (
              <Select.Option value={c.id} key={c.id}>
                {c.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item required rules={baseRules} label='Serviço' name='serviceID'>
          <Select
            onChange={handleServiceSelected}
            placeholder='Selecione um serviço'
          >
            {servicesQuery.data?.map((c) => (
              <Select.Option value={c.id} key={c.id}>
                {c.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item required label='Data' name='date'>
          <DatePicker
            onChange={handleDateSelected}
            disabled={!selectedColaborator}
            showTime={{ showSecond: false }}
            disabledTime={disabledTime}
          />
        </Form.Item>

        <Row>
          <Col>
            <Form.Item label='Data fim' name='endDate'>
              <DatePicker disabled showTime={{ showSecond: false }} />
            </Form.Item>
          </Col>
          <Col>
            <Form.Item label='Duração em minutos' name='durationInMinutes'>
              <Input disabled />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col md={12} span={24} className='hp-pr-sm-0 hp-pr-12'>
            <Button block type='primary' htmlType='submit'>
              {intl.formatMessage({ id: 'create' })}
            </Button>
          </Col>

          <Col md={12} span={24} className='hp-mt-sm-12 hp-pl-sm-0 hp-pl-12'>
            <Button block onClick={props.handleCancel}>
              {intl.formatMessage({ id: 'cancel' })}
            </Button>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default BookForm;
